/**
 * PipelineStatusPanel — Displays pipeline execution status overview.
 *
 * Renders:
 * - Status count cards (succeeded, failed, running, timed out)
 * - Processing rate (executions/hour)
 * - Recent executions table with semantic HTML
 * - Refresh button and last-fetched timestamp
 * - Loading skeleton and error/retry states
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.6, 1.7, 1.8, 10.3
 */

import React from 'react';
import type { PipelineStatusResponse, PipelineExecution } from '../../types/ai-pipeline';
import { formatDuration, truncateText } from '../../utils/format-duration';

export interface PipelineStatusPanelProps {
  readonly status: PipelineStatusResponse | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly onRefresh: () => void;
}

const STATUS_BADGE_CLASSES: Record<PipelineExecution['status'], string> = {
  SUCCEEDED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  RUNNING: 'bg-blue-100 text-blue-800',
  TIMED_OUT: 'bg-yellow-100 text-yellow-800',
  ABORTED: 'bg-gray-100 text-gray-800',
};

function formatLocalTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function PipelineStatusPanel({
  status,
  isLoading,
  error,
  onRefresh,
}: PipelineStatusPanelProps): React.ReactElement {
  if (isLoading && !status) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label="Loading pipeline status">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
        <div className="h-6 w-48 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded-md bg-muted" />
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="rounded-md border border-destructive bg-destructive/10 p-4" role="alert" aria-live="assertive">
        <p className="text-sm text-destructive">{error}</p>
        <button
          type="button"
          className="mt-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onRefresh}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!status) {
    return <div />;
  }

  const { counts, recentExecutions, processingRatePerHour, fetchedAt } = status;

  return (
    <div className="space-y-4">
      {/* Status count cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatusCard label="Succeeded" count={counts.succeeded} className="border-green-200 bg-green-50" />
        <StatusCard label="Failed" count={counts.failed} className="border-red-200 bg-red-50" />
        <StatusCard label="Running" count={counts.running} className="border-blue-200 bg-blue-50" />
        <StatusCard label="Timed Out" count={counts.timedOut} className="border-yellow-200 bg-yellow-50" />
      </div>

      {/* Processing rate */}
      <p className="text-sm text-muted-foreground">
        {processingRatePerHour} executions/hour
      </p>

      {/* Recent executions table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th scope="col" className="px-2 py-2 text-left font-medium">Execution ID</th>
              <th scope="col" className="px-2 py-2 text-left font-medium">Start Time</th>
              <th scope="col" className="px-2 py-2 text-left font-medium">Status</th>
              <th scope="col" className="px-2 py-2 text-left font-medium">Duration</th>
              <th scope="col" className="px-2 py-2 text-left font-medium">Failure Reason</th>
            </tr>
          </thead>
          <tbody>
            {recentExecutions.map((execution) => (
              <tr key={execution.executionId} className="border-b">
                <td className="px-2 py-2 font-mono text-xs">{execution.executionId}</td>
                <td className="px-2 py-2">{formatLocalTime(execution.startTime)}</td>
                <td className="px-2 py-2">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASSES[execution.status]}`}>
                    {execution.status}
                  </span>
                </td>
                <td className="px-2 py-2">{formatDuration(execution.durationMs)}</td>
                <td className="px-2 py-2 text-xs text-muted-foreground">
                  {execution.failureReason ? truncateText(execution.failureReason, 200) : '\u2014'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Refresh button and last updated */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          onClick={onRefresh}
          disabled={isLoading}
        >
          Refresh
        </button>
        <p className="text-xs text-muted-foreground">
          Last updated: {formatLocalTime(fetchedAt)}
        </p>
      </div>
    </div>
  );
}

/** A single status count card. */
function StatusCard({
  label,
  count,
  className,
}: {
  readonly label: string;
  readonly count: number;
  readonly className: string;
}): React.ReactElement {
  return (
    <div className={`rounded-md border p-4 ${className}`}>
      <p className="text-2xl font-bold">{count}</p>
      <p className="text-sm font-medium">{label}</p>
    </div>
  );
}
